import type { QueryClient } from '@tanstack/react-query';
import type { TeammateTopic, TrendTopic } from '@mg/shared';
import { withTeammateTopic } from './teammateTopics';

/**
 * How the Trend Topics caches change when the user adds or removes one of their topics. Kept out of
 * the panel so the decisions below can be tested against a real query cache.
 */

/**
 * After the user adds a teammate's topic as their own. The new topic goes into their list at once
 * (so its chip leaves the suggestions without a wait), and their list and digest refresh.
 *
 * The teammates list is deliberately NOT refetched. The server would answer without this topic (it
 * leaves out what the user already has), and the chip the user may want back - the undo - would be
 * gone from the cache.
 */
export function afterTopicAdopted(qc: QueryClient, tenantKey: string, added: TrendTopic): void {
  qc.setQueryData<TrendTopic[]>(['trend-topics', tenantKey], (old) =>
    old && !old.some((t) => t.id === added.id) ? [...old, added] : old,
  );
  void qc.invalidateQueries({ queryKey: ['trend-topics', tenantKey] });
  // Adding a topic regenerates the digest - refresh Social Trends too.
  void qc.invalidateQueries({ queryKey: ['brief', tenantKey] });
}

/**
 * After the user removes one of their topics. It leaves their list at once. When it came from a
 * teammate's chip earlier on this tab (`cameFrom`), that chip goes straight back into the
 * suggestions, whatever happened to the teammates list since - the undo. The teammates list is then
 * refetched anyway: a topic the user no longer has may be one other teammates track too.
 */
export function afterTopicRemoved(
  qc: QueryClient,
  tenantKey: string,
  removed: Pick<TrendTopic, 'id'>,
  cameFrom?: TeammateTopic,
): void {
  qc.setQueryData<TrendTopic[]>(['trend-topics', tenantKey], (old) =>
    old?.filter((t) => t.id !== removed.id),
  );
  if (cameFrom) {
    qc.setQueryData<TeammateTopic[]>(['teammate-topics', tenantKey], (old) =>
      withTeammateTopic(old ?? [], cameFrom),
    );
  }
  void qc.invalidateQueries({ queryKey: ['trend-topics', tenantKey] });
  void qc.invalidateQueries({ queryKey: ['brief', tenantKey] });
  void qc.invalidateQueries({ queryKey: ['teammate-topics', tenantKey] });
}
